import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, GlassWater, Package, Sun } from "lucide-react";
import { FadeUp, MaskedLines } from "@/components/Reveal";

const HERO_IMAGE = "/hero-product.jpg";

const FEATURES = [
  { icon: Sun, label: "Morning ritual" },
  { icon: GlassWater, label: "Mix & drink" },
  { icon: Package, label: "Easy sachet format" },
];

const HUB = [
  {
    num: "01",
    eyebrow: "The problem & the product",
    title: "Struggling with acne?",
    blurb: "Understand the impact of breakouts and meet Vibe, your simple morning ritual.",
    to: "/struggling",
    id: "hub-card-problem",
  },
  {
    num: "02",
    eyebrow: "Before & after",
    title: "See the results.",
    blurb: "A gallery reserved for verified before-and-after photos and real testimonials.",
    to: "/results",
    id: "hub-card-results",
  },
  {
    num: "03",
    eyebrow: "How to use",
    title: "One sachet. One morning.",
    blurb: "A straightforward morning routine, with or after breakfast, every day.",
    to: "/routine",
    id: "hub-card-routine",
  },
  {
    num: "04",
    eyebrow: "Questions",
    title: "Good questions, honest answers.",
    blurb: "Everything you need to know about Vibe, claims, and our guarantees.",
    to: "/faqs",
    id: "hub-card-faqs",
  },
];

export default function Home() {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 800], [0, 50]);

  return (
    <div data-testid="page-home">
      {/* ---------- Frame 1: Hero ---------- */}
      <section className="grid lg:grid-cols-2 items-stretch lg:h-[calc(100svh-6.9rem)] overflow-hidden">
        <div className="px-5 sm:px-10 lg:pl-[6vw] xl:pl-[7vw] lg:pr-12 pt-[5.75rem] lg:pt-0 pb-10 lg:pb-0 flex flex-col justify-center">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-[0.7rem] sm:text-xs uppercase tracking-[0.26em] text-[#4A6B56] mb-5"
          >
            Small sachet. Fresh start.
          </motion.p>
          <h1 className="font-display text-[clamp(2.3rem,4vw,3.9rem)] leading-[1.05] text-[#1B4332]">
            <MaskedLines
              delay={0.25}
              lines={["A little morning", "ritual.", <span key="l3" className="text-[#4F7A63]">A whole new vibe.</span>]}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
            className="mt-4 max-w-md text-lg text-[#4A6B56] leading-relaxed"
          >
            Meet Vibe. A drinkable powder sachet made for your morning skin-care routine.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-7 flex flex-wrap gap-9 sm:gap-14"
          >
            {FEATURES.map((f) => (
              <div key={f.label} className="flex flex-col items-start gap-3">
                <span className="grid place-items-center h-12 w-12 rounded-full border border-[#C6DCCB] text-[#1B4332]">
                  <f.icon size={19} strokeWidth={1.5} />
                </span>
                <span className="text-sm text-[#4A6B56]">{f.label}</span>
              </div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7"
          >
            <Link
              to="/shop"
              data-testid="hero-shop-cta"
              className="animate-cta-glow group inline-flex items-center gap-2.5 rounded-full bg-[#1B4332] text-[#E8F3EB] pl-9 pr-7 h-14 text-lg font-semibold hover:bg-[#2D5A44] hover:scale-[1.03] transition-all duration-300"
            >
              Meet your daily Vibe
              <ArrowUpRight size={19} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </motion.div>
        </div>

        <div className="relative overflow-hidden h-[42vh] lg:h-full">
          <motion.img
            src={HERO_IMAGE}
            alt="Vibe carton with acne-care sachets and a glass of water on a white pedestal"
            style={{ y: heroY }}
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1.1 }}
            transition={{ duration: 1.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 h-full w-full object-cover"
            data-testid="hero-product-image"
          />
        </div>
      </section>

      {/* ---------- Explore Vibe ---------- */}
      <section className="bg-[#1B4332] pt-10 lg:pt-12 pb-16 lg:pb-20" data-testid="home-hub-band">
        <div className="mx-auto max-w-[88rem] px-5 sm:px-10">
          <FadeUp className="mb-9">
            <div>
              <p className="text-[0.7rem] uppercase tracking-[0.26em] text-[#A7C8B2] mb-4">Explore Vibe</p>
              <h2 className="font-display text-[clamp(1.9rem,3.4vw,3rem)] leading-[1.05] text-[#E8F3EB]">
                Start where you are.
              </h2>
            </div>
            <p className="text-sm text-[#A7C8B2] mt-3" data-testid="pick-a-page-label">
              Pick a page and begin.
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-2 gap-5">
            {HUB.map((card, i) => (
              <FadeUp key={card.to} delay={i * 0.08} className="h-full">
                <Link
                  to={card.to}
                  data-testid={card.id}
                  className="animate-card-highlight group flex flex-col rounded-xl bg-white p-7 sm:p-8 h-full cursor-pointer ring-2 ring-[#A7C8B2] transition-all duration-500 hover:-translate-y-1 hover:bg-white"
                  style={{ boxShadow: "0 24px 50px -24px rgba(0,0,0,0.55)" }}
                >
                  <div className="flex items-start justify-between gap-5 mb-4">
                    <div>
                      {card.id === "hub-card-problem" && (
                        <span
                          data-testid="hub-start-here-pill"
                          className="inline-block mb-2 rounded-full bg-[#1B4332] text-[#E8F3EB] text-[0.62rem] font-bold uppercase tracking-[0.16em] px-3 py-1"
                        >
                          Start here
                        </span>
                      )}
                      <p className="text-[0.68rem] font-bold uppercase tracking-[0.18em] text-[#4A6B56] pt-0.5">
                        {card.eyebrow}
                      </p>
                    </div>
                    <span className="grid place-items-center h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-[#12291C] ring-2 ring-[#A7C8B2]/70 font-display text-2xl sm:text-[1.7rem] font-bold text-[#E8F3EB] shadow-[0_0_0_6px_rgba(18,41,28,0.35)] transition-transform duration-500 group-hover:scale-110 group-hover:ring-[#E8F3EB]">
                      {card.num}
                    </span>
                  </div>
                  <h3 className="font-display text-3xl sm:text-4xl text-[#1B4332] leading-[1.15] underline-offset-4 group-hover:underline">
                    {card.title}
                  </h3>
                  <p className="mt-3 max-w-sm text-[0.95rem] text-[#4A6B56] leading-relaxed">{card.blurb}</p>
                  <span className="mt-auto pt-6 inline-flex items-center gap-1.5 text-[#1B4332] font-semibold text-[0.9rem]">
                    Open
                    <ArrowUpRight
                      size={15}
                      className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </span>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
